/**
 * Transfer windows (1.1.0): named weekly open hours, shared by storage migrations and tiering rules.
 * GET needs storage.manage (the tiering dialog lists them); writes need admin.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TransferWindowBody, TransferWindowDTO } from '@prism/contracts';
import { api } from '../client';
import { queryKeys } from '../queryKeys';

export function useTransferWindows(enabled = true) {
  return useQuery({
    queryKey: queryKeys.transferWindows(),
    queryFn: () => api<{ items: TransferWindowDTO[] }>('/api/v1/transfer-windows'),
    enabled,
  });
}

/** Create, replace and delete; each refreshes the windows, and the rules and migrations that show a window's name. */
export function useTransferWindowMutations() {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: queryKeys.transferWindows() });
    void qc.invalidateQueries({ queryKey: queryKeys.migrations() });
    void qc.invalidateQueries({ queryKey: ['storage-rules'] });
  };
  return {
    create: useMutation({
      mutationFn: (body: TransferWindowBody) => api<TransferWindowDTO>('/api/v1/transfer-windows', { method: 'POST', json: body }),
      onSuccess: refresh,
    }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: TransferWindowBody }) => api<TransferWindowDTO>(`/api/v1/transfer-windows/${id}`, { method: 'PUT', json: body }),
      onSuccess: refresh,
    }),
    /** 409 WINDOW_IN_USE while a migration or a tiering rule uses the window. */
    remove: useMutation({
      mutationFn: (id: string) => api<{ ok: true }>(`/api/v1/transfer-windows/${id}`, { method: 'DELETE' }),
      onSuccess: refresh,
    }),
  };
}
